import { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { authApi } from '../api/auth';
import { configApi } from '../api/config';
import { ApiError } from '../api/client';
import { Field, FormError } from '../components/Field';
import AuthLayout from '../components/AuthLayout';
import { GoogleButton } from '../components/GoogleButton';
import { safeReturnUrl } from '../lib/returnUrl';
import { useAuthStore, useHasAccount } from '../state/authStore';

export default function LoginPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const setUser = useAuthStore((s) => s.setUser);
  const hasAccount = useHasAccount();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: config } = useQuery({ queryKey: ['config'], queryFn: configApi.get });

  const returnUrl = safeReturnUrl(params.get('returnUrl'));
  const googleError = params.get('error');

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      await authApi.login({ email, password, rememberMe });
      // Knowing who is signed in is what moves the page on — see the redirect below.
      setUser(await authApi.me());
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setError(t('auth.emailNotConfirmed'));
      } else if (e instanceof ApiError && e.status === 429) {
        setError(e.message);
      } else {
        setError(t('auth.invalidCredentials'));
      }
    } finally {
      setBusy(false);
    }
  }

  // One rule for both ways of getting here signed in: having just submitted the form, and opening
  // /login with a live session. The second used to show an empty sign-in form to somebody who was
  // already in, which reads as "you have been signed out". A demo session is left alone — its
  // visitor may well be here to sign into their real account.
  if (hasAccount) {
    return <Navigate to={returnUrl} replace />;
  }

  return (
    <AuthLayout>
      <section className="card card--narrow">
        <h1>{t('auth.signIn')}</h1>
        <p className="muted auth__lead">{t('auth.loginLead')}</p>

        {/* First, not last: it is the one-click way in, and under the form it was being found only
            by people who had already typed a password. */}
        {config?.googleEnabled && (
          <>
            <GoogleButton href={authApi.googleStartUrl(returnUrl)} />
            <p className="or muted">{t('auth.or')}</p>
          </>
        )}

        {googleError === 'disabled' && <FormError message={t('auth.accountDisabled')} />}
        {googleError === 'google' && <FormError message={t('common.error')} />}

        <form onSubmit={submit}>
          <Field
            label={t('auth.email')}
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Field
            label={t('auth.password')}
            type="password"
            autoComplete="current-password"
            required
            labelAction={<Link to="/forgot-password">{t('auth.forgotPassword')}</Link>}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <label className="field__check">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            <span>{t('auth.rememberMe')}</span>
          </label>

          <FormError message={error} />

          <button className="btn btn--primary btn--block" type="submit" disabled={busy}>
            {busy ? t('common.loading') : t('auth.signIn')}
          </button>
        </form>

        <p className="muted auth__switch">
          {t('auth.noAccount')} <Link to="/register">{t('auth.signUp')}</Link>
        </p>
      </section>
    </AuthLayout>
  );
}
